import { Message } from '@/enums/message.enum';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setMessages } from '@/redux/slices/app.slice';
import {
  removeFile,
  resetMessages,
  uploadFileSuccess,
  watchFileUploads
} from '@/redux/slices/chatbox.slice';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { Button, Upload, UploadProps } from 'antd';
import { useState } from 'react';

type UploadTokenItemProps = {
  lable: string;
  description: string;
  uploadEndpoint?: string;
  fileType: TypeUpload; // Required prop để xác định loại file
  onUpload?: (file: File, url?: string) => void; // Optional callback
  removeFile?: () => void; // Optional callback
  fileFormat?: string[];
};

export const UploadTokenItem = (props: UploadTokenItemProps) => {
  const {
    lable,
    description,
    uploadEndpoint = 'chatbot/upload-file',
    fileType,
    onUpload,
    removeFile: onRemoveFile,
    fileFormat = ['.csv', '.xls', '.xlsx', '.xlsm', '.xltx', '.xltm', '.xlsb', '.txt']
  } = props;

  const dispatch = useAppDispatch();
  const [uploading, setUploading] = useState(false);
  const uploadedFile = useAppSelector(watchFileUploads);
  const existingFile = uploadedFile[fileType];

  const getMimeTypesFromFormat = (formats: string[]) => {
    const typeFileMap: Record<string, string[]> = {
      '.csv': ['text/csv', 'application/csv'],
      '.xls': ['application/vnd.ms-excel'],
      '.xlsx': [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ],
      '.xlsm': ['application/vnd.ms-excel.sheet.macroEnabled.12'],
      '.xltx': [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.template'
      ],
      '.xltm': ['application/vnd.ms-excel.template.macroEnabled.12'],
      '.xlsb': ['application/vnd.ms-excel.sheet.binary.macroEnabled.12'],
      '.txt': ['text/plain']
    };

    return formats.reduce((acc: string[], format) => {
      const mimeTypes = typeFileMap[format.toLowerCase()];
      if (mimeTypes) {
        acc.push(...mimeTypes);
      }
      return acc;
    }, []);
  };

  const ALLOWED_TYPES = getMimeTypesFromFormat(fileFormat);

  const beforeUpload = (file: File): boolean | typeof Upload.LIST_IGNORE => {
    const isValidType = ALLOWED_TYPES.includes(file.type);
    const fileExtension = file.name
      .slice(file.name.lastIndexOf('.'))
      .toLowerCase();

    if (
      !isValidType ||
      !fileFormat.map((f) => f.toLowerCase()).includes(fileExtension)
    ) {
      const formatText = fileFormat.join(', ');
      dispatch(
        setMessages([
          {
            type: Message.ERROR,
            message: `Chỉ chấp nhận file định dạng: ${formatText}`,
            description:
              'Vui lòng kiểm tra lại định dạng file trước khi tải lên.'
          }
        ])
      );
      return false;
    }

    return true;
  };

  const customRequest = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('file', file);

      const response = await apiFetcher.post(uploadEndpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress({ percent });
          }
        }
      });

      if (response.status === 200) {
        dispatch(
          uploadFileSuccess({
            type: fileType,
            url: response.data || '',
            name: file.name
          })
        );
      }
      setUploading(false);

      // Callback success for Antd Upload
      onSuccess(response, file);

      // Optional callback for parent component
      onUpload?.(file, response?.url || response?.data?.url);
    } catch (error: any) {
      setUploading(false);
      const errorMsg =
        error?.data?.message || error?.message || 'Upload failed';
      dispatch(
        setMessages([
          {
            type: Message.ERROR,
            message: `${file.name} tải lên thất bại: ${errorMsg}`,
            description: ''
          }
        ])
      );

      // Callback error for Antd Upload
      onError(error);
    } finally {
      setUploading(false);
      dispatch(resetMessages());
    }
  };

  const propsUpload: UploadProps = {
    name: 'file',
    customRequest,
    showUploadList: false,
    beforeUpload: (file) => {
      const isValid = beforeUpload(file);
      if (!isValid) {
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    onChange(info) {
      if (info.file.status === 'uploading') {
        setUploading(true);
      }
    },
    accept: fileFormat.join(',')
  };

  const handleRemoveFile = () => {
    setUploading(false);

    dispatch(removeFile({ type: fileType }));

    onRemoveFile?.();
  };

  return (
    <div css={rootStyles}>
      <div css={lableStyles}>{lable}</div>

      <>
        {uploading ? (
          <div css={uploadInputStyles}>
            <span>Đang tải lên...</span>
          </div>
        ) : existingFile?.name ? (
          <div css={uploadInputStyles}>
            <span css={fileNameStyles}>{existingFile.name}</span>
            <Button
              type='primary'
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemoveFile}
            />
          </div>
        ) : (
          <div css={uploadInputStyles}>{description}</div>
        )}
      </>

      <Upload {...propsUpload}>
        <Button
          type='primary'
          css={buttonStyles}
          icon={<UploadOutlined />}
          disabled={uploading}
          loading={uploading}
        >
          {uploading ? 'Đang tải...' : 'Tải lên'}
        </Button>
      </Upload>
    </div>
  );
};

const rootStyles = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  width: 100%;
`;

const lableStyles = css`
  font-weight: bold;
  font-size: 1.4rem;
  flex: 1;
  min-width: 0;
`;

const uploadInputStyles = css`
  border: 1px solid var(--gray-light-color);
  border-radius: 0.8rem;
  padding: 0.5rem 1rem;
  height: 4rem;
  background: var(--gray-light-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 3;
  min-width: 0;
`;

const fileNameStyles = css`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 0.5rem;
`;

const buttonStyles = css`
  flex: 1;
  min-width: 0;
`;

export default UploadTokenItem;
